export type FlatSegmentRule = {
  field?: string
  operator?: string
  value?: unknown
}

export type FlatSegmentRuleChange = {
  field?: string
  operator?: string
  fromValue?: unknown
  toValue?: unknown
}

type SegmentRuleNode =
  | {
      type: 'rule'
      field?: string
      operator?: string
      value?: unknown
    }
  | {
      type: 'group'
      logic?: 'AND' | 'OR'
      rules?: SegmentRuleNode[]
    }

function normalizeScalarForSig(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  try {
    return typeof v === 'object' ? JSON.stringify(v) : String(v)
  } catch {
    return String(v)
  }
}

export function flattenSegmentRules(nodes: any[]): FlatSegmentRule[] {
  const out: FlatSegmentRule[] = []

  const walk = (n: any) => {
    if (!n || typeof n !== 'object') return
    if (n.type === 'group' && Array.isArray(n.rules)) {
      for (const child of n.rules) walk(child)
      return
    }

    // Treat as leaf rule (even if type is missing but shape matches)
    if (n.type === 'rule' || n.field || n.operator) {
      out.push({ field: n.field, operator: n.operator, value: n.value })
    }
  }

  for (const n of nodes || []) walk(n)
  return out
}

export function segmentRuleSig(r: FlatSegmentRule): string {
  return `rule|${normalizeScalarForSig(r.field)}|${normalizeScalarForSig(r.operator)}|${normalizeScalarForSig(r.value)}`
}

export function diffSegment(publishedSeg: any, draftSeg: any): {
  nameChanged: { from: string; to: string } | null
  logicChanged: { from: 'AND' | 'OR'; to: 'AND' | 'OR' } | null
  rulesChanged: FlatSegmentRuleChange[]
  rulesAdded: FlatSegmentRule[]
  rulesRemoved: FlatSegmentRule[]
} {
  const fromName = String(publishedSeg?.name ?? '')
  const toName = String(draftSeg?.name ?? '')

  const fromLogic: 'AND' | 'OR' = publishedSeg?.ruleset?.logic === 'OR' ? 'OR' : 'AND'
  const toLogic: 'AND' | 'OR' = draftSeg?.ruleset?.logic === 'OR' ? 'OR' : 'AND'

  const pubRules = flattenSegmentRules(publishedSeg?.ruleset?.rules || [])
  const drRules = flattenSegmentRules(draftSeg?.ruleset?.rules || [])

  const pubSet = new Set(pubRules.map(segmentRuleSig))
  const drSet = new Set(drRules.map(segmentRuleSig))

  let rulesAdded = drRules.filter((r) => !pubSet.has(segmentRuleSig(r)))
  let rulesRemoved = pubRules.filter((r) => !drSet.has(segmentRuleSig(r)))

  // If a rule "removed" and a rule "added" share the same field+operator,
  // treat it as a value change (from -> to) rather than showing both.
  const keyForChange = (r: FlatSegmentRule) => `${String(r.field ?? '')}|${String(r.operator ?? '')}`
  const sortByValueSig = (a: FlatSegmentRule, b: FlatSegmentRule) =>
    normalizeScalarForSig(a.value).localeCompare(normalizeScalarForSig(b.value))

  const addedByKey = new Map<string, FlatSegmentRule[]>()
  for (const r of rulesAdded) {
    const k = keyForChange(r)
    if (!addedByKey.has(k)) addedByKey.set(k, [])
    addedByKey.get(k)!.push(r)
  }
  for (const arr of addedByKey.values()) arr.sort(sortByValueSig)

  const removedByKey = new Map<string, FlatSegmentRule[]>()
  for (const r of rulesRemoved) {
    const k = keyForChange(r)
    if (!removedByKey.has(k)) removedByKey.set(k, [])
    removedByKey.get(k)!.push(r)
  }
  for (const arr of removedByKey.values()) arr.sort(sortByValueSig)

  const rulesChanged: FlatSegmentRuleChange[] = []
  for (const [k, removedArr] of removedByKey.entries()) {
    const addedArr = addedByKey.get(k) || []
    if (removedArr.length === 0 || addedArr.length === 0) continue

    const pairs = Math.min(removedArr.length, addedArr.length)
    for (let i = 0; i < pairs; i += 1) {
      const from = removedArr[i]
      const to = addedArr[i]
      rulesChanged.push({
        field: from.field ?? to.field,
        operator: from.operator ?? to.operator,
        fromValue: from.value,
        toValue: to.value,
      })
    }
  }

  if (rulesChanged.length > 0) {
    const changedKeys = new Set(rulesChanged.map((c) => `${String(c.field ?? '')}|${String(c.operator ?? '')}`))

    // Remove the paired items from added/removed lists (best effort, by signature)
    const pairedRemovedSigs = new Set<string>()
    const pairedAddedSigs = new Set<string>()
    for (const c of rulesChanged) {
      pairedRemovedSigs.add(`rule|${normalizeScalarForSig(c.field)}|${normalizeScalarForSig(c.operator)}|${normalizeScalarForSig(c.fromValue)}`)
      pairedAddedSigs.add(`rule|${normalizeScalarForSig(c.field)}|${normalizeScalarForSig(c.operator)}|${normalizeScalarForSig(c.toValue)}`)
    }

    rulesRemoved = rulesRemoved.filter((r) => {
      const k = keyForChange(r)
      if (!changedKeys.has(k)) return true
      return !pairedRemovedSigs.has(segmentRuleSig(r))
    })
    rulesAdded = rulesAdded.filter((r) => {
      const k = keyForChange(r)
      if (!changedKeys.has(k)) return true
      return !pairedAddedSigs.has(segmentRuleSig(r))
    })
  }

  return {
    nameChanged: fromName !== toName ? { from: fromName || '—', to: toName || '—' } : null,
    logicChanged: fromLogic !== toLogic ? { from: fromLogic, to: toLogic } : null,
    rulesChanged,
    rulesAdded,
    rulesRemoved,
  }
}

